import { WorkoutSession } from '../types';
import { EXERCISE_CONFIG } from '../constants';

/**
 * Generates a high-quality 4:5 Portrait Image (1080x1350) for social sharing.
 * Uses a clean, high-contrast dark theme with strict typography.
 */
export const generateWorkoutSticker = async (
  session: WorkoutSession
): Promise<string> => {
  // Critical: Wait for fonts to load so the canvas draws the correct typeface
  await document.fonts.ready;

  return new Promise((resolve, reject) => {
    const width = 1080;
    const height = 1350;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject("Canvas context not available");
      return;
    }

    // --- THEME CONFIG ---
    const bg = '#09090b'; // Zinc-950 (Rich Black) - kept for text colors
    const textPrimary = '#FFFFFF';
    const textSecondary = '#a1a1aa'; // Zinc-400
    // Dynamic accent based on score
    const accent = session.accuracyScore >= 80 ? '#4ade80' : (session.accuracyScore >= 60 ? '#facc15' : '#ef4444');
    
    // 1. Background Fill - REMOVED for Transparent Export
    ctx.clearRect(0, 0, width, height); 

    // 2. Decorative Grid (Subtle Tech Feel)
    ctx.strokeStyle = '#18181b'; // Zinc-900
    ctx.lineWidth = 2;
    const gridSize = 135; // Fits 1080 exactly 8 times
    
    ctx.beginPath();
    for (let x = 0; x <= width; x += gridSize) {
      ctx.moveTo(x, 0); ctx.lineTo(x, height); 
    }
    for (let y = 0; y <= height; y += gridSize) {
       ctx.moveTo(0, y); ctx.lineTo(width, y); 
    }
    ctx.stroke();

    const padding = 80;

    // 3. Header
    // Logo Box
    ctx.fillStyle = textPrimary;
    ctx.fillRect(padding, padding, 100, 100);
    ctx.fillStyle = bg;
    ctx.font = '900 70px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('L', padding + 50, padding + 54);

    // Brand Name
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = textPrimary;
    ctx.font = '900 60px Inter, sans-serif';
    ctx.fillText('LEVEL UP', padding + 130, padding + 15);
    
    // Tagline
    ctx.fillStyle = textSecondary;
    ctx.font = '500 28px Roboto Mono, monospace';
    ctx.fillText('FITNESS PROTOCOL', padding + 135, padding + 85);

    // Date (Top Right)
    const dateObj = new Date(session.date);
    const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    
    ctx.textAlign = 'right';
    ctx.fillStyle = textPrimary;
    ctx.font = '900 40px Inter, sans-serif';
    ctx.fillText(`${month} ${day}`, width - padding, padding + 30);
    
    ctx.fillStyle = textSecondary;
    ctx.font = '500 28px Roboto Mono, monospace';
    ctx.fillText(`${year}`, width - padding, padding + 85);


    // 4. Hero Content (Center)
    const centerY = height * 0.48;

    // Background Exercise Name (Stroke Text)
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#27272a'; // Zinc-800
    ctx.font = '900 200px Inter, sans-serif';
    const exerciseName = EXERCISE_CONFIG[session.exercise]?.name?.toUpperCase() || 'WORKOUT';
    ctx.strokeText(exerciseName, width / 2, centerY - 100);
    ctx.restore();

    // Big Rep Count
    ctx.fillStyle = textPrimary;
    ctx.font = '900 450px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Shadow for depth
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 50;
    ctx.fillText(`${session.reps}`, width / 2, centerY + 60);
    ctx.shadowBlur = 0;

    // "REPS" Label
    ctx.fillStyle = accent;
    ctx.font = '700 60px Roboto Mono, monospace';
    ctx.letterSpacing = '15px';
    ctx.fillText('REPETITIONS', width / 2, centerY + 280);


    // 5. Footer Stats Grid
    const footerY = height - 350;
    
    // Separator Line
    ctx.fillStyle = '#27272a';
    ctx.fillRect(padding, footerY - 40, width - (padding * 2), 2);

    // Stat 1: Form Score
    ctx.textAlign = 'left';
    ctx.fillStyle = textSecondary;
    ctx.font = '500 30px Roboto Mono, monospace';
    ctx.fillText('ACCURACY', padding, footerY + 20);
    
    ctx.fillStyle = accent;
    ctx.font = '900 110px Inter, sans-serif';
    ctx.fillText(`${Math.round(session.accuracyScore)}%`, padding, footerY + 130);

    // Stat 2: Duration
    ctx.textAlign = 'right';
    ctx.fillStyle = textSecondary;
    ctx.font = '500 30px Roboto Mono, monospace';
    ctx.fillText('DURATION', width - padding, footerY + 20);

    let timeStr = '';
    if (session.mode === 'TIME_ATTACK' && session.targetDuration) {
        timeStr = `${Math.floor(session.targetDuration / 60)} MIN`;
    } else {
        const m = Math.floor(session.durationSeconds / 60);
        const s = session.durationSeconds % 60;
        timeStr = `${m}:${s.toString().padStart(2, '0')}`;
    }

    ctx.fillStyle = textPrimary;
    ctx.font = '900 110px Inter, sans-serif';
    ctx.fillText(timeStr, width - padding, footerY + 130);


    // 6. Footer URL
    ctx.textAlign = 'center';
    ctx.fillStyle = '#3f3f46'; // Zinc-700
    ctx.font = '500 24px Roboto Mono, monospace';
    ctx.fillText('GENERATED BY LEVEL UP // APP', width / 2, height - 60);

    resolve(canvas.toDataURL('image/png'));
  });
};

export const downloadSticker = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};